/**
 * MCP Governance Page (F032)
 * Route: /mcp-governance | Nav: Model > MCP Governance
 *
 * Control-plane governance for MCP (Model Context Protocol) servers and the tools they expose —
 * access footprint, risk tier, and approval state. Built on the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and `-behaviours.context.md`, mirroring
 * `src/pages/AgentRegistryPage.tsx` (F031) for conventions.
 *
 * Binds to `McpGovernanceController` (`api/portal/mcp-governance`). Register Server / Register
 * Tool forms live in `src/features/mcp-governance/forms/`.
 *
 * Deliberately lighter than the Agent Registry page: a simple stat-card row replaces the
 * agents-by-environment stacked bar (no equivalent breakdown dimension for MCP servers).
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Text,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Tooltip,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Field,
  Textarea,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  AddRegular,
  ChevronRightRegular,
  GaugeRegular,
  CheckmarkCircle24Regular,
  DismissRegular,
  Warning24Regular,
  Server24Regular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mcpGovernanceApi } from '../services/api';
import type {
  ApprovalState,
  McpAccessLevel,
  McpServer,
  McpServerDetail,
  McpServerListFilter,
  McpServerStatus,
  McpTool,
  RiskTier,
} from '../types';
import { McpServerRegisterForm, McpToolRegisterForm } from '../features/mcp-governance/forms';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  useDebouncedValue,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import {
  DetailDrawer,
  DetailSection,
  DetailField,
  DetailMetadataGrid,
} from '../components/common/DetailDrawer';
import { MetricCard } from '../components/design-system';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

const APPROVAL_STATE_OPTIONS: ApprovalState[] = ['NotRequired', 'Pending', 'Approved', 'Rejected', 'Expired'];
const RISK_TIER_OPTIONS: RiskTier[] = ['Unknown', 'Low', 'Medium', 'High', 'Critical'];
const SERVER_STATUS_OPTIONS: McpServerStatus[] = ['Active', 'Quarantined', 'Decommissioned'];

// ---------------------------------------------------------------------------
// Badge color mapping — semantic map, not raw hex (per List & Grid Standard §15). `riskTierColor`
// and `approvalStateColor` are copied verbatim from `AgentRegistryPage.tsx` (F031) — same enums,
// same semantics, kept feature-local rather than shared to avoid cross-feature coupling.
// ---------------------------------------------------------------------------

const riskTierColor = (tier: RiskTier): BadgeProps['color'] => {
  switch (tier) {
    case 'Critical': return 'danger';
    case 'High': return 'severe';
    case 'Medium': return 'warning';
    case 'Low': return 'success';
    case 'Unknown':
    default: return 'informative';
  }
};

/** Ordinal rank matching the C# `RiskTier` enum (Unknown=0 .. Critical=4) — the default sort
 *  comparator's `localeCompare` would otherwise sort risk tiers alphabetically, which is not
 *  severity order. */
const RISK_TIER_RANK: Record<RiskTier, number> = { Unknown: 0, Low: 1, Medium: 2, High: 3, Critical: 4 };

function getMcpServerSortValue(item: McpServer, field: string): unknown {
  if (field === 'riskTier') return RISK_TIER_RANK[item.riskTier];
  return (item as unknown as Record<string, unknown>)[field];
}

const approvalStateColor = (state: ApprovalState): BadgeProps['color'] => {
  switch (state) {
    case 'Approved': return 'success';
    case 'Pending': return 'warning';
    case 'Rejected': return 'danger';
    case 'Expired': return 'severe';
    case 'NotRequired':
    default: return 'informative';
  }
};

const serverStatusColor = (status: McpServerStatus): BadgeProps['color'] => {
  switch (status) {
    case 'Active': return 'success';
    case 'Quarantined': return 'severe';
    case 'Decommissioned': return 'subtle';
    default: return 'informative';
  }
};

/** Compact abbreviation for the Access column's per-row indicators. */
const accessAbbrev = (level: McpAccessLevel): string => {
  switch (level) {
    case 'Full': return 'Full';
    case 'ReadWrite': return 'RW';
    case 'ReadOnly': return 'RO';
    case 'None':
    default: return '—';
  }
};

// ---------------------------------------------------------------------------
// Filter state — 5 server-side facets, encoded into the shared `filters` URL param (§17/§19),
// `field:op:value` pairs joined by ';' — same convention as AgentRegistryPage / PolicyRunsPage.
// ---------------------------------------------------------------------------

interface McpServerFilterState {
  approvalState: ApprovalState | '';
  minRiskTier: RiskTier | '';
  environment: string;
  status: McpServerStatus | '';
  ownerTeam: string;
}

const EMPTY_MCP_SERVER_FILTERS: McpServerFilterState = {
  approvalState: '',
  minRiskTier: '',
  environment: '',
  status: '',
  ownerTeam: '',
};

function parseMcpServerFilters(raw: string): McpServerFilterState {
  const state: McpServerFilterState = { ...EMPTY_MCP_SERVER_FILTERS };
  if (!raw) return state;
  for (const part of raw.split(';')) {
    const [field, , ...rest] = part.split(':');
    const value = rest.join(':');
    if (!field || !value) continue;
    switch (field) {
      case 'approvalState': state.approvalState = value as ApprovalState; break;
      case 'minRiskTier': state.minRiskTier = value as RiskTier; break;
      case 'environment': state.environment = value; break;
      case 'status': state.status = value as McpServerStatus; break;
      case 'ownerTeam': state.ownerTeam = value; break;
      default: break;
    }
  }
  return state;
}

function serializeMcpServerFilters(state: McpServerFilterState): string {
  const parts: string[] = [];
  if (state.approvalState) parts.push(`approvalState:eq:${state.approvalState}`);
  if (state.minRiskTier) parts.push(`minRiskTier:eq:${state.minRiskTier}`);
  if (state.environment) parts.push(`environment:eq:${state.environment}`);
  if (state.status) parts.push(`status:eq:${state.status}`);
  if (state.ownerTeam) parts.push(`ownerTeam:eq:${state.ownerTeam}`);
  return parts.join(';');
}

function toMcpServerListFilter(state: McpServerFilterState): McpServerListFilter {
  return {
    approvalState: state.approvalState || undefined,
    minRiskTier: state.minRiskTier || undefined,
    environment: state.environment || undefined,
    status: state.status || undefined,
    ownerTeam: state.ownerTeam || undefined,
  };
}

/**
 * Server-side skip/take pagination is assumed clamped by `McpGovernanceController` at parity
 * with the Agent Registry's `AgentsController` (1-200) — the endpoint contract given does not
 * document the clamp explicitly. `threshold` is set to match that assumption so the adaptive
 * grid's client/server split never straddles a partially-clamped page; adjust this constant if
 * the actual server clamp differs (per the "never filter/sort/search a partial set" invariant —
 * `.github/context/list-grid-standard-behaviours.context.md` §10).
 */
const MCP_SERVER_GRID_THRESHOLD = 200;

async function fetchMcpServersPage(
  filter: McpServerListFilter,
  params: ListQueryParams
): Promise<PagedResult<McpServer>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result = await mcpGovernanceApi.getServers(filter, skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  filterDropdown: {
    minWidth: '9rem',
  },
  textFilterInput: {
    minWidth: '10rem',
  },
  // rem, not a spacing token — caps the Register Server / Register Tool dialog surface height so
  // a long form scrolls internally instead of growing the page (matches AgentRegistryPage).
  formDialogSurface: {
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  subRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  subRowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  summarySection: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  statCardRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  cappedNote: {
    color: tokens.colorNeutralForeground3,
  },
  unapprovedCard: {
    flexShrink: 0,
  },
  unapprovedBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  unapprovedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  unapprovedRowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  unapprovedEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  accessRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
});

const crumbs: Crumb[] = [{ label: 'MCP Governance' }];

// ---------------------------------------------------------------------------
// McpSummaryPanel — a simple stat-card row (no stacked-bar chart — there is no equivalent
// breakdown dimension for MCP servers the way F031 has agents-by-environment). Aggregates are
// computed client-side from one bounded list fetch, same pattern as `AgentSummaryPanel`.
// ---------------------------------------------------------------------------

const MCP_SUMMARY_SAMPLE = 200;

const McpSummaryPanel: FC = () => {
  const styles = useStyles();
  const summary = useQuery({
    queryKey: ['mcp-servers-summary', MCP_SUMMARY_SAMPLE],
    queryFn: () => mcpGovernanceApi.getServers({}, 0, MCP_SUMMARY_SAMPLE),
  });
  // Same key as UnapprovedUsagePanel — deduped by React Query, so the sweep runs once.
  const unapproved = useQuery({
    queryKey: ['mcp-unapproved-usage'],
    queryFn: () => mcpGovernanceApi.getUnapprovedUsage(),
  });

  if (summary.isLoading) {
    return (
      <div className={styles.summarySection}>
        <LoadingSkeleton rows={1} columns={4} />
      </div>
    );
  }
  if (summary.isError || !summary.data) {
    return (
      <div className={styles.summarySection}>
        <ErrorState message="Unable to load the MCP governance summary." onRetry={() => summary.refetch()} />
      </div>
    );
  }

  const { items, totalCount } = summary.data;
  const capped = totalCount > items.length;
  const pendingCount = items.filter((s) => s.approvalState === 'Pending').length;
  const highRiskCount = items.filter((s) => s.riskTier === 'High' || s.riskTier === 'Critical').length;
  const unapprovedCount = unapproved.data?.length ?? 0;

  return (
    <div className={styles.summarySection}>
      <div className={styles.statCardRow}>
        <MetricCard
          title="Total Servers"
          value={totalCount}
          icon={<Server24Regular />}
          accentColor={tokens.colorBrandForeground1}
        />
        <MetricCard
          title="Pending Approval"
          value={capped ? `${pendingCount}+` : pendingCount}
          icon={<Warning24Regular />}
          accentColor={tokens.colorPaletteMarigoldForeground1}
        />
        <MetricCard
          title="High-Risk"
          value={capped ? `${highRiskCount}+` : highRiskCount}
          icon={<Warning24Regular />}
          accentColor={tokens.colorPaletteDarkOrangeForeground1}
          secondaryMetrics={[{ label: 'Tiers', value: 'High + Critical' }]}
        />
        <MetricCard
          title="Unapproved Usage"
          value={unapprovedCount}
          icon={<Warning24Regular />}
          accentColor={tokens.colorPaletteRedForeground1}
        />
      </div>
      {capped && (
        <Caption1 className={styles.cappedNote}>
          Stats reflect the first {items.length} of {totalCount} servers.
        </Caption1>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// UnapprovedUsagePanel — "Shadow / Unapproved MCP Usage" (sibling above the list frame),
// modelled on `ShadowAiPanel` (F031 P4) but for MCP traffic instead of agent telemetry.
// ---------------------------------------------------------------------------

const UnapprovedUsagePanel: FC = () => {
  const styles = useStyles();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['mcp-unapproved-usage'],
    queryFn: () => mcpGovernanceApi.getUnapprovedUsage(),
  });

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className={styles.unapprovedEmpty}>
        <Spinner size="tiny" />
        <Body2>Checking for unapproved MCP usage…</Body2>
      </div>
    );
  } else if (isError) {
    body = (
      <div className={styles.unapprovedEmpty}>
        <Warning24Regular />
        <Body2>Unable to load unapproved MCP usage.</Body2>
        <Button appearance="secondary" size="small" onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  } else if (!data || data.length === 0) {
    body = (
      <div className={styles.unapprovedEmpty}>
        <CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
        <Body2>No unapproved MCP servers or tools observed.</Body2>
      </div>
    );
  } else {
    body = (
      <div className={styles.subList}>
        {data.map((usage, index) => (
          <div key={`${usage.callerRef}-${index}`} className={styles.unapprovedRow}>
            <div className={styles.unapprovedRowMain}>
              <Text weight="semibold">{usage.callerRef}</Text>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {usage.reason}
                {usage.mcpServerId ? ` · Server #${usage.mcpServerId}` : ''}
                {usage.mcpToolId ? ` · Tool #${usage.mcpToolId}` : ''}
              </Caption1>
            </div>
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
              <Badge appearance="tint" color={usage.isRegistered ? 'warning' : 'danger'}>
                {usage.isRegistered ? 'Registered, Unapproved' : 'Unregistered'}
              </Badge>
              <Badge appearance="tint" color={riskTierColor(usage.inferredRisk)}>{usage.inferredRisk}</Badge>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className={styles.unapprovedCard}>
      <CardHeader
        image={<Warning24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />}
        header={<Text weight="semibold">Shadow / Unapproved MCP Usage</Text>}
        description={<Caption1>MCP servers and tools observed in traffic but unregistered or unapproved</Caption1>}
      />
      <div className={styles.unapprovedBody}>{body}</div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// McpServerDetailDrawer — server fields + its tools sub-list, with per-tool "Assess" and a
// "Register Tool" affordance. `toasterId` is the SAME id the page-level `Toaster` renders with —
// multiple `useToastController(id)` calls targeting one Toaster surface is a supported Fluent
// pattern, so the drawer's mutation (Assess) can dispatch without a second `<Toaster>` mount.
// ---------------------------------------------------------------------------

const McpServerDetailDrawer: FC<{
  serverId: number | null;
  onClose: () => void;
  onRegisterTool: () => void;
  toasterId: string;
}> = ({ serverId, onClose, onRegisterTool, toasterId }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  const { data, isLoading, isError } = useQuery<McpServerDetail>({
    queryKey: ['mcp-server-detail', serverId],
    queryFn: () => mcpGovernanceApi.getServer(serverId!),
    enabled: serverId !== null,
  });

  const assessMutation = useMutation({
    mutationFn: (toolId: number) => mcpGovernanceApi.assessTool(toolId),
    onSuccess: (assessment) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-server-detail', serverId] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Risk Assessed: {assessment.riskTier}</ToastTitle>
          <ToastBody>
            {assessment.reasons.length > 0 ? assessment.reasons.join('; ') : 'No contributing factors recorded.'}
          </ToastBody>
        </Toast>,
        { intent: 'success', timeout: 6000 }
      );
    },
  });

  const approveToolMutation = useMutation({
    mutationFn: (toolId: number) => mcpGovernanceApi.approveTool(toolId),
    onSuccess: (tool) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-server-detail', serverId] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Tool Approved</ToastTitle>
          <ToastBody>{tool.toolName} is now approved.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
    },
  });

  return (
    <DetailDrawer open={serverId !== null} onClose={onClose} title="MCP Server Details" size="large">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading server…" />
        </div>
      )}
      {isError && <ErrorState message="Unable to load this server's detail." />}
      {data && (
        <>
          <DetailSection title={data.server.serverName}>
            <Text style={{ fontFamily: 'monospace' }}>{data.server.serverUrl}</Text>
            <DetailMetadataGrid>
              <DetailField label="Environment">
                <Text>{data.server.environment ?? '—'}</Text>
              </DetailField>
              <DetailField label="Status">
                <Badge appearance="tint" color={serverStatusColor(data.server.status)}>{data.server.status}</Badge>
              </DetailField>
              <DetailField label="Approval State">
                <Badge appearance="tint" color={approvalStateColor(data.server.approvalState)}>
                  {data.server.approvalState}
                </Badge>
              </DetailField>
              <DetailField label="Risk Tier">
                <Badge appearance="tint" color={riskTierColor(data.server.riskTier)}>{data.server.riskTier}</Badge>
              </DetailField>
              <DetailField label="Owner">
                <Text>
                  {data.server.ownerId ?? '—'}
                  {data.server.ownerTeam ? ` (${data.server.ownerTeam})` : ''}
                </Text>
              </DetailField>
              <DetailField label="Network Access">
                <Badge appearance="outline">{data.server.networkAccessLevel}</Badge>
              </DetailField>
              <DetailField label="Filesystem Access">
                <Badge appearance="outline">{data.server.filesystemAccessLevel}</Badge>
              </DetailField>
              <DetailField label="Secrets Access">
                <Badge appearance="tint" color={data.server.secretsAccessRequired ? 'danger' : 'subtle'}>
                  {data.server.secretsAccessRequired ? 'Required' : 'Not Required'}
                </Badge>
              </DetailField>
              <DetailField label="Data Access Level">
                <Text>{data.server.dataAccessLevel}</Text>
              </DetailField>
              <DetailField label="Tool Count">
                <Text>{data.server.toolCount}</Text>
              </DetailField>
              <DetailField label="Created">
                <Text>{new Date(data.server.createdAt).toLocaleString()}</Text>
              </DetailField>
              <DetailField label="Updated">
                <Text>{data.server.updatedAt ? new Date(data.server.updatedAt).toLocaleString() : '—'}</Text>
              </DetailField>
              <DetailField label="External ID">
                <Text style={{ fontFamily: 'monospace' }}>{data.server.externalId}</Text>
              </DetailField>
            </DetailMetadataGrid>
          </DetailSection>

          <DetailSection title={`Tools (${data.tools.length})`} divider>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button appearance="secondary" icon={<AddRegular />} size="small" onClick={onRegisterTool}>
                Register Tool
              </Button>
            </div>
            {data.tools.length === 0 ? (
              <Caption1>No tools registered on this server yet.</Caption1>
            ) : (
              <div className={styles.subList}>
                {data.tools.map((tool: McpTool) => {
                  const isAssessing = assessMutation.isPending && assessMutation.variables === tool.id;
                  const isApprovingTool = approveToolMutation.isPending && approveToolMutation.variables === tool.id;
                  return (
                    <div key={tool.id} className={styles.subRow}>
                      <div className={styles.subRowHeader}>
                        <Text weight="semibold">{tool.toolName}</Text>
                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                          <Badge appearance="tint" color={riskTierColor(tool.riskTier)}>{tool.riskTier}</Badge>
                          <Badge appearance="tint" color={approvalStateColor(tool.approvalState)}>
                            {tool.approvalState}
                          </Badge>
                        </div>
                      </div>
                      {tool.description && <Caption1>{tool.description}</Caption1>}
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        Data: {tool.dataAccessLevel}
                        {tool.canWrite ? ' · Write' : ''}
                        {tool.canDelete ? ' · Delete' : ''}
                        {tool.canExecuteCode ? ' · Execute Code' : ''}
                        {tool.requiresApproval ? ' · Requires Approval' : ''}
                      </Caption1>
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                        {tool.approvalState === 'Pending' && (
                          <Button
                            appearance="primary"
                            size="small"
                            icon={isApprovingTool ? <Spinner size="tiny" /> : <CheckmarkCircle24Regular />}
                            disabled={isApprovingTool}
                            onClick={() => approveToolMutation.mutate(tool.id)}
                          >
                            Approve
                          </Button>
                        )}
                        <Button
                          appearance="secondary"
                          size="small"
                          icon={isAssessing ? <Spinner size="tiny" /> : <GaugeRegular />}
                          disabled={isAssessing}
                          onClick={() => assessMutation.mutate(tool.id)}
                        >
                          Assess
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DetailSection>
        </>
      )}
    </DetailDrawer>
  );
};

// ---------------------------------------------------------------------------
// McpGovernancePage
// ---------------------------------------------------------------------------

export const McpGovernancePage: FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('mcp-governance-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [selectedServerId, setSelectedServerId] = useState<number | null>(null);

  // Register Server dialog.
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [registerDirty, setRegisterDirty] = useState(false);

  const closeRegisterDialog = (force = false) => {
    if (!force && registerDirty && !window.confirm('Discard unsaved changes?')) return;
    setIsRegisterDialogOpen(false);
    setRegisterDirty(false);
  };

  // Register Tool dialog — opened from the detail drawer, bound to the server currently open.
  const [isRegisterToolDialogOpen, setIsRegisterToolDialogOpen] = useState(false);
  const [registerToolDirty, setRegisterToolDirty] = useState(false);

  const closeRegisterToolDialog = (force = false) => {
    if (!force && registerToolDirty && !window.confirm('Discard unsaved changes?')) return;
    setIsRegisterToolDialogOpen(false);
    setRegisterToolDirty(false);
  };

  // Reject Server dialog — small reason capture, only reachable while approvalState === 'Pending'.
  const [rejectingServerId, setRejectingServerId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState<string | undefined>();

  const closeRejectDialog = () => {
    setRejectingServerId(null);
    setRejectReason('');
    setRejectReasonError(undefined);
  };

  const handleServerRegistered = (server: McpServer) => {
    queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
    queryClient.invalidateQueries({ queryKey: ['mcp-servers-summary'] });
    queryClient.invalidateQueries({ queryKey: ['mcp-unapproved-usage'] });
    dispatchToast(
      <Toast>
        <ToastTitle>Server Registered</ToastTitle>
        <ToastBody>{server.serverName} has been registered for governance.</ToastBody>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    closeRegisterDialog(true);
  };

  const handleToolRegistered = (tool: McpTool) => {
    queryClient.invalidateQueries({ queryKey: ['mcp-server-detail', tool.mcpServerId] });
    queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
    queryClient.invalidateQueries({ queryKey: ['mcp-unapproved-usage'] });
    dispatchToast(
      <Toast>
        <ToastTitle>Tool Registered</ToastTitle>
        <ToastBody>{tool.toolName} has been registered.</ToastBody>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    closeRegisterToolDialog(true);
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => mcpGovernanceApi.approveServer(id),
    onSuccess: (server) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-summary'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-server-detail', server.id] });
      dispatchToast(
        <Toast>
          <ToastTitle>Server Approved</ToastTitle>
          <ToastBody>{server.serverName} has been approved.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => mcpGovernanceApi.rejectServer(id, reason),
    onSuccess: (server) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-grid'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers-summary'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-server-detail', server.id] });
      dispatchToast(
        <Toast>
          <ToastTitle>Server Rejected</ToastTitle>
          <ToastBody>{server.serverName} was rejected.</ToastBody>
        </Toast>,
        { intent: 'warning', timeout: 5000 }
      );
      closeRejectDialog();
    },
  });

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      setRejectReasonError('Reason is required');
      return;
    }
    if (rejectingServerId === null) return;
    rejectMutation.mutate({ id: rejectingServerId, reason: rejectReason.trim() });
  };

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'serverName:asc' });

  const filterState = parseMcpServerFilters(state.filters);
  const mcpServerListFilter = toMcpServerListFilter(filterState);

  const [environmentInput, setEnvironmentInput] = useState(filterState.environment);
  useEffect(() => {
    setEnvironmentInput(filterState.environment);
    // Re-sync only when the URL's environment filter changes externally (e.g. back/forward).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState.environment]);
  const debouncedEnvironment = useDebouncedValue(environmentInput, 300);
  useEffect(() => {
    if (debouncedEnvironment === filterState.environment) return;
    setFilters(serializeMcpServerFilters({ ...filterState, environment: debouncedEnvironment }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEnvironment]);

  const [ownerTeamInput, setOwnerTeamInput] = useState(filterState.ownerTeam);
  useEffect(() => {
    setOwnerTeamInput(filterState.ownerTeam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState.ownerTeam]);
  const debouncedOwnerTeam = useDebouncedValue(ownerTeamInput, 300);
  useEffect(() => {
    if (debouncedOwnerTeam === filterState.ownerTeam) return;
    setFilters(serializeMcpServerFilters({ ...filterState, ownerTeam: debouncedOwnerTeam }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOwnerTeam]);

  const updateFilter = <K extends keyof McpServerFilterState>(key: K, value: McpServerFilterState[K]) =>
    setFilters(serializeMcpServerFilters({ ...filterState, [key]: value }));

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<McpServer>({
    // The filters string is folded INTO queryKeyBase (not passed as `filtersSerialized`) because
    // these facets are applied SERVER-SIDE by `McpGovernanceController` — a filter change must
    // produce a fresh probe/page fetch, not an in-memory re-filter of an already-filtered set.
    queryKeyBase: ['mcp-servers-grid', state.filters],
    fetchPage: (params) => fetchMcpServersPage(mcpServerListFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    // Search has no server-side counterpart on this endpoint — it only takes effect in adaptive
    // client mode (total <= MCP_SERVER_GRID_THRESHOLD), scanning the already server-filtered rows.
    searchFields: ['serverName', 'ownerTeam', 'environment'],
    sortState,
    getValue: getMcpServerSortValue,
    threshold: MCP_SERVER_GRID_THRESHOLD,
  });

  const {
    items: servers,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after filter/search.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const actionButtonCount = 3; // chevron detail + Approve + Reject — Approve/Reject hidden (not
  // disabled) on rows where approvalState !== 'Pending'; the column width sizes to the worst case.
  const columnCount = 7; // name, environment, riskTier, approvalState, access, tools, actions

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the MCP server registry." onRetry={refetch} />;
  } else if (servers.length === 0) {
    listBody = (
      <EmptyState
        icon={<Server24Regular />}
        title="No MCP Servers Registered"
        description="No servers match the current filters, or no MCP servers have been registered yet for this tenant."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="MCP server registry">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'serverName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('serverName')}
                onClick={() => toggleSort('serverName')}
                style={{ width: '100%' }}
              >
                Server Name
              </TableHeaderCell>
              <TableHeaderCell>Environment</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'riskTier' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('riskTier')}
                onClick={() => toggleSort('riskTier')}
              >
                Risk Tier
              </TableHeaderCell>
              <TableHeaderCell>Approval</TableHeaderCell>
              <TableHeaderCell>Access</TableHeaderCell>
              <TableHeaderCell>Tools</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {servers.map((item) => {
              const isApproving = approveMutation.isPending && approveMutation.variables === item.id;
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div>
                      <Text weight="semibold">{item.serverName}</Text>
                      <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                        {item.serverUrl}
                      </Caption1>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="outline">{item.environment ?? '—'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={riskTierColor(item.riskTier)}>{item.riskTier}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={approvalStateColor(item.approvalState)}>
                      {item.approvalState}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className={styles.accessRow}>
                      <Tooltip content={`Network access: ${item.networkAccessLevel}`} relationship="label">
                        <Badge appearance="outline" size="small">N:{accessAbbrev(item.networkAccessLevel)}</Badge>
                      </Tooltip>
                      <Tooltip content={`Filesystem access: ${item.filesystemAccessLevel}`} relationship="label">
                        <Badge appearance="outline" size="small">F:{accessAbbrev(item.filesystemAccessLevel)}</Badge>
                      </Tooltip>
                      {item.secretsAccessRequired && (
                        <Tooltip content="Requires secrets access" relationship="label">
                          <Badge appearance="tint" color="danger" size="small">Secrets</Badge>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.toolCount}</Body2>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                      {item.approvalState === 'Pending' && (
                        <Tooltip content="Approve Server" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={isApproving ? <Spinner size="tiny" /> : <CheckmarkCircle24Regular />}
                            size="small"
                            aria-label="Approve server"
                            disabled={isApproving}
                            onClick={() => approveMutation.mutate(item.id)}
                          />
                        </Tooltip>
                      )}
                      {item.approvalState === 'Pending' && (
                        <Tooltip content="Reject Server" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<DismissRegular />}
                            size="small"
                            aria-label="Reject server"
                            onClick={() => setRejectingServerId(item.id)}
                          />
                        </Tooltip>
                      )}
                      <Button
                        appearance="subtle"
                        icon={<ChevronRightRegular />}
                        size="small"
                        aria-label="View server details"
                        onClick={() => setSelectedServerId(item.id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      {/* Header stat cards (F032 — no equivalent stacked-bar breakdown dimension for MCP servers). */}
      <McpSummaryPanel />

      {/* Shadow / unapproved MCP usage sweep — sibling above the list frame, non-scrolling. */}
      <UnapprovedUsagePanel />

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={crumbs}
              busy={isFetching && !isLoading}
              subtitle="MCP servers and the tools they expose, governed with risk-based approval"
            />
          }
          toolbar={
            <ListToolbar
              primaryActions={
                <Button appearance="primary" icon={<AddRegular />} onClick={() => setIsRegisterDialogOpen(true)}>
                  Register Server
                </Button>
              }
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search servers…"
              extraFilters={
                <>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Approval State"
                    value={filterState.approvalState}
                    onOptionSelect={(_, data) =>
                      updateFilter('approvalState', (data.optionValue || '') as ApprovalState | '')
                    }
                  >
                    <Option value="">All Approval States</Option>
                    {APPROVAL_STATE_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Min Risk Tier"
                    value={filterState.minRiskTier}
                    onOptionSelect={(_, data) =>
                      updateFilter('minRiskTier', (data.optionValue || '') as RiskTier | '')
                    }
                  >
                    <Option value="">Any Risk Tier</Option>
                    {RISK_TIER_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Status"
                    value={filterState.status}
                    onOptionSelect={(_, data) =>
                      updateFilter('status', (data.optionValue || '') as McpServerStatus | '')
                    }
                  >
                    <Option value="">All Statuses</Option>
                    {SERVER_STATUS_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                  <Input
                    className={styles.textFilterInput}
                    placeholder="Environment"
                    value={environmentInput}
                    onChange={(_, data) => setEnvironmentInput(data.value)}
                  />
                  <Input
                    className={styles.textFilterInput}
                    placeholder="Owner Team"
                    value={ownerTeamInput}
                    onChange={(_, data) => setOwnerTeamInput(data.value)}
                  />
                </>
              }
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="mcp-governance"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="mcp-governance-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      <McpServerDetailDrawer
        serverId={selectedServerId}
        onClose={() => setSelectedServerId(null)}
        onRegisterTool={() => setIsRegisterToolDialogOpen(true)}
        toasterId={toasterId}
      />

      {/* Register Server */}
      <Dialog
        open={isRegisterDialogOpen}
        onOpenChange={(_, data) => (data.open ? setIsRegisterDialogOpen(true) : closeRegisterDialog())}
      >
        <DialogSurface className={styles.formDialogSurface}>
          <DialogTitle>Register Server</DialogTitle>
          <DialogBody>
            <DialogContent>
              <McpServerRegisterForm
                onSuccess={handleServerRegistered}
                onCancel={() => closeRegisterDialog()}
                onDirtyChange={setRegisterDirty}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Register Tool — bound to the server currently open in the detail drawer */}
      <Dialog
        open={isRegisterToolDialogOpen}
        onOpenChange={(_, data) => (data.open ? setIsRegisterToolDialogOpen(true) : closeRegisterToolDialog())}
      >
        <DialogSurface className={styles.formDialogSurface}>
          <DialogTitle>Register Tool</DialogTitle>
          <DialogBody>
            <DialogContent>
              {selectedServerId !== null && (
                <McpToolRegisterForm
                  mcpServerId={selectedServerId}
                  onSuccess={handleToolRegistered}
                  onCancel={() => closeRegisterToolDialog()}
                  onDirtyChange={setRegisterToolDirty}
                />
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Reject Server */}
      <Dialog
        open={rejectingServerId !== null}
        onOpenChange={(_, data) => {
          if (!data.open) closeRejectDialog();
        }}
      >
        <DialogSurface>
          <DialogTitle>Reject MCP Server</DialogTitle>
          <DialogBody>
            <DialogContent>
              <Field
                label="Reason"
                required
                validationState={rejectReasonError ? 'error' : undefined}
                validationMessage={rejectReasonError}
              >
                <Textarea
                  value={rejectReason}
                  onChange={(_, data) => {
                    setRejectReason(data.value);
                    if (rejectReasonError) setRejectReasonError(undefined);
                  }}
                  placeholder="Why is this server being rejected?"
                  resize="vertical"
                  disabled={rejectMutation.isPending}
                />
              </Field>
              <div className={styles.dialogActions}>
                <Button appearance="secondary" onClick={closeRejectDialog} disabled={rejectMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleRejectSubmit}
                  disabled={rejectMutation.isPending}
                  icon={rejectMutation.isPending ? <Spinner size="tiny" /> : undefined}
                >
                  {rejectMutation.isPending ? 'Rejecting…' : 'Reject Server'}
                </Button>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
